/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { CodCreateForm } from '../models/cod-create.model';
import { ICodWithSubtype, IExtendedOrderLine, Region } from '@cod-monorepo/interfaces';
import { StepConfig } from '../../shared/types/step-config.type';
import { RegionStepConfig } from '../components/steps/region/region.config';
import {
    AddCurrentCodLine,
    AddErrorMessage,
    AttachCodLine,
    BackToForm,
    CopyCod,
    EvaluateSteps,
    LoadAttachments,
    LoadCodById,
    LoadOrderLineItems,
    OpenForm,
    RemoveErrorMessage,
    RemoveTopErrorMessage,
    ResetErrorMessages,
    ResetForm,
    ResetReferenceNumber,
    SetOrderLineItems,
    SetViewMode,
    SubmittCod,
    UpdateFormValidity
} from './cod.action';
import { OrderLineService } from '../services/order-line.service';
import { catchError, forkJoin, from, map, mergeMap, Observable, switchMap, tap, throwError, toArray } from 'rxjs';
import { WorkflowError } from '../../shared/enums/workflow-error';
import { ViewMode } from '../../shared/enums/view-mode';
import { CodMappingOperations } from '../operations/cod-mapping.operations';
import { ResetForm as NgxsResetForm, UpdateFormValue } from '@ngxs/form-plugin';
import { CodService } from '../../shared/services/cod.service';
import { CodStepOperations } from '../operations/cod-steps.operations';
import { GeneralCod } from '../models/cod.model';
import { CodSubmittEndpoint } from '../config/cod-submit.config';
import { AttachmentService } from '../services/attachment.service';
import { Router } from '@angular/router';
import { SelectableOrderLine, withOrderLineSelectionIds } from '../models/selectable-order-line.model';

interface CodStateModel {
    form: {
        model?: CodCreateForm;
        dirty: boolean;
        status: string;
        errors: { [key: string]: string };
    };
    selectedCod: GeneralCod | null;
    steps: StepConfig[];
    queryingReferenceNumber: boolean;
    referenceNumberFound: boolean | null;
    errorMessages: WorkflowError[];
    orderLineItems: SelectableOrderLine[];
    viewMode: ViewMode;
    codLines: CodCreateForm[];
    openIndex: number | null;
    submitting: boolean;
    submitted: boolean;
    loading: boolean;
    referenceNumberDomesticStatus: string;
    referenceNumberInternationalStatus: string;
    affectedItemsStatus: string;
    disableValidation: boolean;
}

@State<CodStateModel>({
    name: 'cod',
    defaults: {
        form: {
            model: undefined,
            dirty: false,
            status: '',
            errors: {}
        },
        selectedCod: null,
        steps: [RegionStepConfig],
        queryingReferenceNumber: false,
        referenceNumberFound: null,
        errorMessages: [],
        orderLineItems: [],
        viewMode: ViewMode.FORM,
        codLines: [],
        openIndex: null,
        submitting: false,
        submitted: false,
        loading: false,
        referenceNumberDomesticStatus: '',
        referenceNumberInternationalStatus: '',
        affectedItemsStatus: '',
        disableValidation: false
    }
})
@Injectable()
export class CodState {
    constructor(
        private orderLineService: OrderLineService,
        private codService: CodService,
        private attachmentService: AttachmentService,
        private router: Router
    ) {}

    @Selector()
    public static selectedCod(state: CodStateModel): GeneralCod | null {
        return state.selectedCod;
    }

    @Selector()
    public static loading(state: CodStateModel): boolean {
        return state.loading;
    }

    @Selector()
    static selectedRegion(state: CodStateModel): Region | null {
        return state.form.model?.region.region || null;
    }

    @Selector()
    static steps(state: CodStateModel): StepConfig[] {
        return state.steps;
    }

    @Selector()
    static queryingReferenceNumber(state: CodStateModel): boolean {
        return state.queryingReferenceNumber;
    }

    @Selector()
    static errorMessages(state: CodStateModel): WorkflowError[] {
        return state.errorMessages;
    }

    @Selector()
    static orderLineItems(state: CodStateModel): SelectableOrderLine[] {
        return state.orderLineItems;
    }

    @Selector()
    static step(state: CodStateModel): number {
        if (state.viewMode === ViewMode.SUMMARY) {
            return 5;
        }

        if (state.viewMode === ViewMode.SUBMITTING) {
            return 6;
        }

        return state.steps.length;
    }

    @Selector()
    static referenceNumberFound(state: CodStateModel): boolean | null {
        return state.referenceNumberFound;
    }

    @Selector()
    static viewMode(state: CodStateModel): ViewMode {
        return state.viewMode;
    }

    @Selector()
    static mappedCodLines(state: CodStateModel): GeneralCod[] {
        return state.codLines.map(form => {
            const { cod } = CodMappingOperations.mapFormToDto(form);
            return cod;
        }) as unknown as GeneralCod[];
    }

    @Selector()
    static disableValidation(state: CodStateModel): boolean {
        return state.disableValidation;
    }

    @Selector()
    static wasFiledWithin40Days(state: CodStateModel): boolean | null {
        return state.form.model?.referenceNumberInternational?.pgiWithin40Days ?? null;
    }

    @Selector()
    static isDomestic(state: CodStateModel): boolean {
        return state.form.model?.region.region === Region.NAM;
    }

    @Action(EvaluateSteps)
    evaluateSteps(ctx: StateContext<CodStateModel>): void {
        const { referenceNumberDomesticStatus, referenceNumberInternationalStatus, form: ngxsForm } = ctx.getState();
        const form = ngxsForm.model;

        const steps = CodStepOperations.evaluateSteps(
            form!,
            referenceNumberDomesticStatus,
            referenceNumberInternationalStatus
        );

        ctx.patchState({
            steps
        });
    }

    @Action(AddErrorMessage)
    setErrorMessage(ctx: StateContext<CodStateModel>, action: AddErrorMessage): void {
        const errorMessages = ctx.getState().errorMessages;

        ctx.patchState({
            errorMessages: [...new Set([action.error, ...errorMessages])]
        });
    }

    @Action(RemoveErrorMessage)
    resetErrorMessage(ctx: StateContext<CodStateModel>, action: RemoveErrorMessage): void {
        const errorMessages = ctx.getState().errorMessages;
        const index = errorMessages.indexOf(action.error);

        if (index > -1) {
            errorMessages.splice(index, 1);
        }

        ctx.patchState({
            errorMessages
        });
    }

    @Action(RemoveTopErrorMessage)
    removeTopErrorMessage(ctx: StateContext<CodStateModel>): void {
        const errorMessages = ctx.getState().errorMessages;
        errorMessages.shift();

        ctx.patchState({
            errorMessages
        });
    }

    @Action(ResetErrorMessages)
    resetErrorMessages(ctx: StateContext<CodStateModel>): void {
        ctx.patchState({
            errorMessages: []
        });
    }

    @Action(LoadOrderLineItems)
    loadOrderLineItems(ctx: StateContext<CodStateModel>, action: LoadOrderLineItems): Observable<IExtendedOrderLine[]> {
        ctx.patchState({
            queryingReferenceNumber: true,
            referenceNumberFound: false
        });

        return this.orderLineService.orderLineExists(action.referenceNumber, action.referenceNumberType).pipe(
            switchMap(({ rowExists }: { rowExists: boolean }) => {
                if (!rowExists) {
                    ctx.patchState({
                        queryingReferenceNumber: false,
                        referenceNumberFound: false
                    });

                    return throwError(() => 'Order line not found');
                }

                return this.orderLineService.getOrderLineItems(action.referenceNumber, action.referenceNumberType);
            }),
            tap(orderLineItems => {
                ctx.patchState({
                    orderLineItems: withOrderLineSelectionIds(orderLineItems),
                    queryingReferenceNumber: false,
                    referenceNumberFound: true
                });

                ctx.dispatch(
                    new UpdateFormValue({
                        value: {
                            affectedItems: {
                                shipToCountryCode: orderLineItems?.[0]?.shipToCountryCode ?? ''
                            }
                        },
                        path: 'cod.form'
                    })
                );
            })
        );
    }

    @Action(SetOrderLineItems)
    setOrderLineItems(ctx: StateContext<CodStateModel>, action: SetOrderLineItems): void {
        ctx.patchState({
            orderLineItems: withOrderLineSelectionIds(action.orderLineItems),
            queryingReferenceNumber: false,
            referenceNumberFound: true
        });
    }

    @Action(AddCurrentCodLine)
    addCurrentCodLine(ctx: StateContext<CodStateModel>): void {
        const codLines = ctx.getState().codLines;
        const form = ctx.getState().form.model;
        const openIndex = ctx.getState().openIndex;

        if (openIndex !== null) {
            codLines[openIndex] = form!;
        } else {
            codLines.push(form!);
        }

        ctx.patchState({
            codLines,
            viewMode: ViewMode.SUMMARY,
            openIndex: null
        });
    }

    @Action(AttachCodLine)
    attachCodLine(ctx: StateContext<CodStateModel>): void {
        ctx.patchState({
            viewMode: ViewMode.FORM
        });
    }

    @Action(OpenForm)
    openForm(ctx: StateContext<CodStateModel>, action: OpenForm): void {
        const codLines = ctx.getState().codLines;
        const form = codLines[action.index];
        const steps = CodStepOperations.evaluateSteps(form!, 'VALID', 'VALID');

        ctx.patchState({
            steps,
            openIndex: action.index
        });

        ctx.dispatch(
            new UpdateFormValue({
                value: form,
                path: 'cod.form'
            })
        );

        ctx.patchState({
            viewMode: ViewMode.FORM
        });
    }

    @Action(SubmittCod)
    submitCod(
        ctx: StateContext<CodStateModel>,
        action: SubmittCod
    ): Observable<ICodWithSubtype<{ issueType: string }>[]> {
        ctx.patchState({
            submitting: true,
            viewMode: ViewMode.SUBMITTING
        });

        const codLines = ctx.getState().codLines;
        const copyId = action.copyId;

        const submitCodLine = (codLine: CodCreateForm) => {
            const { cod, documents } = CodMappingOperations.mapFormToDto(codLine);
            const endpoint = CodSubmittEndpoint[cod.natureOfComplaint];

            const documentsToUpload = documents.filter(document => !!document.file);
            const documentsToCopy = copyId ? documents.filter(document => !document.file) : [];

            return this.codService.submitCod(cod, endpoint).pipe(
                switchMap(response =>
                    from(documentsToUpload).pipe(
                        mergeMap(document =>
                            this.attachmentService.uploadAttachment(response.id!, document.key, document.file)
                        ),
                        toArray(),
                        map(() => response)
                    )
                ),
                switchMap(response =>
                    from(documentsToCopy).pipe(
                        mergeMap(document =>
                            this.attachmentService.copyAttachment(
                                `${copyId}/${document.key}/${document.name}`,
                                `${response.id}/${document.key}/${document.name}`
                            )
                        ),
                        toArray(),
                        map(() => response)
                    )
                )
            );
        };

        return from(codLines).pipe(
            mergeMap(codLine => submitCodLine(codLine)),
            toArray(),
            tap(() => {
                ctx.dispatch(new ResetForm());
                this.router.navigate(['/']);
            }),
            catchError(error => {
                console.error('Submission error:', error);
                return throwError(() => error);
            })
        );
    }

    @Action(LoadCodById)
    public loadCodById(ctx: StateContext<CodStateModel>, action: LoadCodById): Observable<GeneralCod> {
        ctx.patchState({
            loading: true
        });

        return this.codService.loadCodById(action.id).pipe(
            tap((cod: GeneralCod) => {
                ctx.patchState({
                    selectedCod: cod,
                    loading: false
                });

                ctx.dispatch(
                    new UpdateFormValue({
                        value: {
                            region: {
                                region: cod.region
                            }
                        } as CodCreateForm,
                        path: 'cod.form'
                    })
                );
            })
        );
    }

    @Action(SetViewMode)
    setViewMode(ctx: StateContext<CodStateModel>, action: SetViewMode): void {
        ctx.patchState({
            viewMode: action.viewMode
        });
    }

    @Action(ResetForm)
    resetForm(ctx: StateContext<CodStateModel>): void {
        ctx.patchState({
            form: {
                model: undefined,
                dirty: false,
                status: '',
                errors: {}
            },
            steps: [RegionStepConfig],
            queryingReferenceNumber: false,
            referenceNumberFound: null,
            errorMessages: [],
            orderLineItems: [],
            viewMode: ViewMode.FORM,
            codLines: [],
            submitting: false
        });

        ctx.dispatch(new NgxsResetForm({ path: 'cod.form' }));
    }

    @Action(UpdateFormValidity)
    updateFormValidity(ctx: StateContext<CodStateModel>, action: UpdateFormValidity): void {
        ctx.patchState({
            [action.form]: action.status
        });
    }

    @Action(LoadAttachments)
    loadAttachments(ctx: StateContext<CodStateModel>, action: LoadAttachments): void {
        this.attachmentService.getAttachments(action.id).subscribe();
    }

    @Action(CopyCod)
    copyCod(ctx: StateContext<CodStateModel>, action: CopyCod): Observable<IExtendedOrderLine[]> {
        ctx.patchState({
            loading: true,
            disableValidation: true
        });

        return forkJoin([
            this.codService.loadCodById(action.id),
            this.attachmentService.getAttachments(action.id)
        ]).pipe(
            tap(([cod, attachments]) => {
                const keys = attachments.map(attachment => attachment.Key);
                const form = CodMappingOperations.mapDtoToForm(cod, keys, false);
                const steps = CodStepOperations.evaluateSteps(form!, 'VALID', 'VALID');

                ctx.dispatch(new LoadOrderLineItems(cod.referenceNumber, cod.referenceNumberType));

                ctx.patchState({
                    selectedCod: cod,
                    loading: false,
                    steps
                });

                ctx.dispatch(
                    new UpdateFormValue({
                        value: form,
                        path: 'cod.form'
                    })
                );
            }),
            switchMap(([cod]) => {
                ctx.patchState({
                    queryingReferenceNumber: true,
                    referenceNumberFound: false
                });

                return this.orderLineService.orderLineExists(cod.referenceNumber, cod.referenceNumberType).pipe(
                    switchMap(({ rowExists }: { rowExists: boolean }) => {
                        if (!rowExists) {
                            ctx.patchState({
                                queryingReferenceNumber: false,
                                referenceNumberFound: false
                            });

                            return throwError(() => 'Order line not found');
                        }

                        return this.orderLineService.getOrderLineItems(cod.referenceNumber, cod.referenceNumberType);
                    }),
                    tap(orderLineItems =>
                        ctx.patchState({
                            orderLineItems: withOrderLineSelectionIds(orderLineItems),
                            queryingReferenceNumber: false,
                            referenceNumberFound: true
                        })
                    )
                );
            }),
            tap(() =>
                ctx.patchState({
                    referenceNumberDomesticStatus: 'VALID',
                    referenceNumberInternationalStatus: 'VALID',
                    disableValidation: false
                })
            )
        );
    }

    @Action(BackToForm)
    backToForm(ctx: StateContext<CodStateModel>): void {
        ctx.patchState({
            viewMode: ViewMode.FORM
        });
    }

    @Action(ResetReferenceNumber)
    resetReferenceNumber(ctx: StateContext<CodStateModel>): void {
        ctx.patchState({
            referenceNumberDomesticStatus: '',
            referenceNumberInternationalStatus: '',
            referenceNumberFound: null,
            queryingReferenceNumber: false
        });
    }
}
